import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Divider,
  Space,
  Typography,
  Spin,
  Popconfirm,
  Modal,
  Radio,
  message,
  Tooltip,
  Steps,
  Badge,
  Tag,
  Empty,
  Table,
} from "antd";
import { PageContentLayout, PageHeaderLayout } from "../../../shared/layout";
import { 
  UsergroupAddOutlined, 
  CheckCircleOutlined, 
  UploadOutlined,
  RocketOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import moment from "moment";

const { Title, Text } = Typography;
const { Step } = Steps;

// Helper function to map DSP names based on providersVisible permission
const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }
  
  // Map to tier names when providersVisible is false
  switch (dspName?.toLowerCase()) {
    case 'liquidm':
      return 'Tier 1';
    case 'nexxen':
      return 'Tier 2';
    default:
      return dspName; // Return original if not mapped
  }
};

const FirstPartyDataDetailView = (props) => {
  const {
    activateHashedEmailList,
    refetchHashedEmailList,
    refetchHashedEmailLists,
    deleteHashedEmailList,
    processDeviceIds,
    updateHashedEmailList,
    updateHashedEmailLatestStatus,
    currentOrg,
    providersVisible,
  } = props;
  const { id } = useParams();

  const [hashedEmailList, setHashedEmailList] = useState({});
  const [loading, setLoading] = useState(false);
  const [backLoading, setBackLoading] = useState(false);
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const [step2DspModalVisible, setStep2DspModalVisible] = useState(false);
  const [selectedDspProvider, setSelectedDspProvider] = useState("liquidm");
  const [step2DspProvider, setStep2DspProvider] = useState("liquidm");
  
  // Auto-switch to Nexxen if phone numbers are detected since LiquidM doesn't support phone numbers
  useEffect(() => {
    if (hashedEmailList.typeOfFirstPartyData === "phones") {
      if (selectedDspProvider === "liquidm") {
        setSelectedDspProvider("nexxen");
      }
      if (step2DspProvider === "liquidm") {
        setStep2DspProvider("nexxen");
      }
    }
  }, [hashedEmailList.typeOfFirstPartyData, selectedDspProvider, step2DspProvider]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusResultsModal, setStatusResultsModal] = useState(false);
  const [statusResults, setStatusResults] = useState(null);
  const history = useHistory();
  const renderLabelValuePair = (label, val) => {
    return (
      <>
        <Space direction="vertical">
          <Text strong style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            {label}
          </Text>
          <Text>{val}</Text>
        </Space>
      </>
    );
  };

  // Progress tracking function
  const updateProgress = async (stepProgress, status, dsp = null) => {
    if (!hashedEmailList.id) return;
    
    try {
      await updateHashedEmailList({
        id: hashedEmailList.id,
        stepProgress: stepProgress,
        status: status,
        ...(dsp && { dsp: dsp.toUpperCase() })
      });
      // Reload data to reflect changes
      await handleLoadData();
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const handleLoadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await refetchHashedEmailList({
        id: id,
      });

      // Check if unauthorized response from controller
      if (response && response.unauthorized) {
        message.warning(response.message);
        history.push('/audience/main/1');
        return;
      }

      if (response && response.data) {
        const hashedEmailList = response.data.hashedEmailLists[0];

        const dspSegmentIds = hashedEmailList.dspSegmentIds[0];

        const emailCount = hashedEmailList?.tag ? parseInt(hashedEmailList?.tag) : 0;

        // Use the actual status field and format it for display, with fallback to legacy activated field
        const status = hashedEmailList.status 
          ? hashedEmailList.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
          : hashedEmailList.activated 
            ? "Activated"
            : "No Status";

        // Use the stepProgress from the database, don't override it
        // The step completion logic will handle different statuses appropriately
        const stepProgress = hashedEmailList.stepProgress || 0;

        const matchRate =
          typeof hashedEmailList.matchRate === 'number' &&
          Number.isFinite(hashedEmailList.matchRate)
            ? hashedEmailList.matchRate.toFixed(2).toLocaleString() + '%'
            : '0%';

        const deviceCount =
          typeof hashedEmailList.deviceCount === 'number' ? hashedEmailList.deviceCount : null;

        const orgName = hashedEmailList.orgs[0].name;

        setHashedEmailList({
          ...hashedEmailList,
          ...dspSegmentIds,
          emailCount,
          deviceCount,
          status,
          matchRate,
          orgName,
          stepProgress,
        });
      }
    } catch (error) {
      message.error('Error loading data ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [refetchHashedEmailList, id, history]);

  const handleStep2DspSelection = async () => {
    try {
      setStep2DspModalVisible(false);
      await handleActivateEmails(step2DspProvider);
    } catch (error) {
      console.error("Error in Step 2 DSP selection:", error);
    }
  };

  const handleActivateEmails = async (dspProvider = null) => {
    try {
      setLoading(true);
      const response = await activateHashedEmailList({
        orgID: currentOrg?.id,
        hashedEmailListIds: [hashedEmailList.id],
        dsp: (dspProvider || step2DspProvider).toUpperCase()
      });
      if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.success
      ) {
        const dsp = dspProvider || step2DspProvider;
        await updateProgress(2, "WAITING_FOR_ACTIVATION", dsp.toUpperCase());
        message.success("Device ID mapping completed successfully!");
        // Reload data to reflect changes
        await handleLoadData();
      } else if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.error
      ) {
        message.error(response.data.activateHashedEmailList.error);
      } else {
        message.error(
          "No device IDs were processed. Please make sure there are emails to process."
        );
      }
    } catch (error) {
      message.error('Error activating emails ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetLatestStatus = async () => {
    try {
      setStatusLoading(true);
      
      if (!hashedEmailList.id) {
        return message.error("Audience not found");
      }
      
      const response = await updateHashedEmailLatestStatus({
        variables: {
          orgID: currentOrg?.id,
          hashedEmailListIds: [hashedEmailList.id]
        }
      });

      if (response && response.data && response.data.updateHashedEmailLatestStatus) {
        let result;
        try {
          // Handle both string and object responses
          if (typeof response.data.updateHashedEmailLatestStatus === 'string') {
            result = JSON.parse(response.data.updateHashedEmailLatestStatus);
          } else {
            result = response.data.updateHashedEmailLatestStatus;
          }
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
          message.error("Error parsing server response");
          return;
        }
        
        if (result && (result.success || result.data)) {
          const responseMessage = result.message || "Status updated successfully!";
          message.success(responseMessage);
          
          // Handle both new and old response formats
          let modalData;
          if (result.data && Array.isArray(result.data)) {
            // New response format with data array
            modalData = {
              success: true,
              message: responseMessage,
              processed: result.data.length,
              successCount: result.data.length,
              errorCount: 0,
              hashedEmailLists: result.data.map(item => ({
                id: item.id,
                name: item.name,
                stepProgress: item.stepProgress,
                status: item.status,
                dsp: item.dsp,
                dspSegmentIds: item.dspSegmentIds || [],
                devicesCount: item.devicesCount,
                createdAt: item.createdAt,
                message: item.message,
                processResult: item.message,
                typeOfFirstPartyData: item.typeOfFirstPartyData,
                lastStepProgress: item.lastStepProgress,
                lastStatus: item.lastStatus
              }))
            };
          } else {
            // Legacy response format for backward compatibility
            modalData = {
              success: true,
              message: responseMessage,
              processed: 1,
              successCount: 1,
              errorCount: 0,
              hashedEmailLists: [{
                id: hashedEmailList.id,
                name: hashedEmailList.name,
                stepProgress: result.stepProgress || hashedEmailList.stepProgress,
                status: result.status || hashedEmailList.status,
                dsp: hashedEmailList.dsp,
                dspSegmentIds: hashedEmailList.dspSegmentIds || [],
                devicesCount: hashedEmailList.devicesCount || hashedEmailList.deviceCount,
                createdAt: hashedEmailList.createdAt,
                message: result.message,
                processResult: result.message || "Status updated successfully",
                typeOfFirstPartyData: result.typeOfFirstPartyData,
                lastStepProgress: result.lastStepProgress,
                lastStatus: result.lastStatus
              }]
            };
          }
          
          setStatusResults(modalData);
          setStatusResultsModal(true);
          
          // Reload data to reflect changes
          await handleLoadData();
        } else {
          message.error(result?.error || "Failed to update status");
        }
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error("Error updating latest status: " + error.message);
      console.error("Error in handleGetLatestStatus:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleProcessDeviceIds = async (dspProvider) => {
    try {
      setLoading(true);
      setProcessModalVisible(false);

      const response = await processDeviceIds({
        variables: { 
          orgID: currentOrg?.id,
          emailListIds: [hashedEmailList.id],
          dsp: dspProvider.toUpperCase() 
        },
      });

      if (response && response.data.processDeviceIds.deviceIdsProcessed > 0) {
        await updateProgress(3, "WAITING_FOR_ACTIVATION", dspProvider.toUpperCase());
        message.success("Email list processed.");
      } else {
        message.warning("No device IDs were processed.");
      }
    } catch (error) {
      message.error("Error processing device ID's " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackNavigation = async () => {
    try {
      setBackLoading(true);
      // Refetch the main table before navigating back
      if (refetchHashedEmailLists) {
        await refetchHashedEmailLists();
      }
    } catch (error) {
      console.error("Error refetching email lists:", error);
    } finally {
      setBackLoading(false);
      history.push("/audience/main/1");
    }
  };

  const handleDeleteAudience = async () => {
    try {
      setLoading(true);
      
      // Check status and stepProgress-based restrictions
      if (hashedEmailList.status === "WAITING_FOR_ACTIVATION" || 
          hashedEmailList.status === "ACTIVATED" ||
          (hashedEmailList.stepProgress && hashedEmailList.stepProgress >= 3)) {
        return message.error("Cannot delete an audience that is in progress or already activated");
      }
      
      await deleteHashedEmailList(hashedEmailList);
      message.success('Audience deleted, redirecting to main page...');
      setTimeout(() => {
        handleBackNavigation();
      }, 1000);
    } catch (err) {
      message.error('Error deleting audience ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const ProcessDeviceIdsButton = () => (
    <Tooltip
      placement="top"
      title={
        <span>
          <strong>Step 2: Activate Device IDs in DSPs</strong>
          <br />
          Publishes the returned device IDs to DSP partners for campaign targeting. Requires manual
          verification—process takes 2–3 days.
        </span>
      }
    >
      <span style={{ display: "inline-block" }}>
        <Button
          style={{ marginRight: "8px" }}
          onClick={() => setProcessModalVisible(true)}
        >
          Activate in DSP
        </Button>
      </span>
    </Tooltip>
  );

  const DeleteAudienceButton = () => (
    <Popconfirm
      title="Are you sure you want to delete this audience?"
      onConfirm={handleDeleteAudience}
      okText="Yes"
      cancelText="No"
    >
      <Button icon={<DeleteOutlined />}>Delete Audience</Button>
    </Popconfirm>
  );

  useEffect(() => {
    handleLoadData();
  }, [handleLoadData]);

  return (
    <React.Fragment>
      <PageHeaderLayout
        titleIcon={<UsergroupAddOutlined />}
        title={hashedEmailList.name || 'Audience'}
        rightContent={[
          <Button key="back" onClick={handleBackNavigation} loading={backLoading} icon={<ArrowLeftOutlined />} style={{ marginTop: '10px' }}>
            Back
          </Button>,
        ]}
      />

      <PageContentLayout>
        <Spin spinning={loading}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Audience Details</span>
                {hashedEmailList && hashedEmailList.processedAt && hashedEmailList.createdAt && 
                 hashedEmailList.processedAt !== null &&
                 moment(hashedEmailList.processedAt).valueOf() !== moment(hashedEmailList.createdAt).valueOf() && (
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Last Updated: {moment(hashedEmailList.processedAt).format('MM/DD/YYYY - h:mm A')}
                  </Text>
                )}
              </div>
            }
            bordered={false}
            style={{ minHeight: "350px" }}
          >
            {hashedEmailList && (
              <Row style={{ height: "100%" }}>
                <Col span={16}>
                  <Row>
                    <Col span={6}>
                      {renderLabelValuePair(
                        hashedEmailList.typeOfFirstPartyData === "phones" ? "Phone Number Count" : "Email Count", 
                        hashedEmailList.emailCount ? Math.ceil(hashedEmailList.emailCount).toLocaleString() : "0"
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        "Devices",
                        hashedEmailList.deviceCount ? Math.ceil(hashedEmailList.deviceCount).toLocaleString() : "0"
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair('Match Rate', hashedEmailList.matchRate)}
                    </Col>
                    <Col span={6}>{renderLabelValuePair('Status', hashedEmailList.status)}</Col>
                  </Row>
                  <Row style={{ marginTop: '2em' }}>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'Created',
                        hashedEmailList && hashedEmailList.createdAt
                          ? moment(hashedEmailList.createdAt).format('MM/DD/YYYY - hh:mma')
                          : '-'
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair('Advertiser', hashedEmailList.orgName)}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'Data Type', 
                        hashedEmailList.typeOfFirstPartyData === "phones" ? "Phone Numbers" : "Email Addresses"
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'CSV File Name',
                        hashedEmailList && hashedEmailList.csvFileName
                          ? hashedEmailList.csvFileName
                          : '-'
                      )}
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '2em' }}>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'DSP Platform',
                        hashedEmailList.dsp ? getDSPDisplayName(hashedEmailList.dsp, providersVisible) : '-'
                      )}
                    </Col>
                    <Col span={6}>
                      {/* Empty column for future expansion */}
                    </Col>
                    <Col span={6}>
                      {/* Empty column for future expansion */}
                    </Col>
                    <Col span={6}>
                      {/* Empty column for future expansion */}
                    </Col>
                  </Row>
                </Col>
                
                {/* Right Column - Step-Based Actions */}
                <Col span={8}>
                  <div style={{ paddingLeft: "24px", borderLeft: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                      <Title level={5} style={{ margin: 0, marginRight: "8px" }}>Audience Activation Progress</Title>
                      <Badge 
                        count={`${hashedEmailList.activated ? 4 : hashedEmailList.stepProgress || 0}/4`} 
                        style={{ backgroundColor: (hashedEmailList.activated || (hashedEmailList.stepProgress || 0) === 4) ? "#52c41a" : "#1890ff" }}
                      />
                    </div>
                    
                    <Steps
                      direction="vertical"
                      size="small"
                      current={-1}
                    >
                      {[
                        { 
                          title: hashedEmailList.typeOfFirstPartyData === "phones" ? "Upload Phone Numbers" : "Upload Emails", 
                          icon: <UploadOutlined />, 
                          description: hashedEmailList.typeOfFirstPartyData === "phones" 
                            ? "Your phone number list has been successfully uploaded and is ready for processing" 
                            : "Your email list has been successfully uploaded and is ready for processing" 
                        },
                        { 
                          title: hashedEmailList.typeOfFirstPartyData === "phones" ? "Convert Phone Numbers to Device IDs" : "Convert Emails to Device IDs", 
                          icon: <UsergroupAddOutlined />, 
                          description: hashedEmailList.typeOfFirstPartyData === "phones"
                            ? "Converting phone numbers into device identifiers through secure hashing and identity matching. This process usually takes about 30 minutes to complete."
                            : "Converting email addresses into device identifiers through secure hashing and identity matching. This process usually takes about 30 minutes to complete." 
                        },
                        { title: "Activate in DSP", icon: <RocketOutlined />, description: "Sending your audience data to DSP partners to enable targeted campaign delivery" },
                        { title: "Activated", icon: <CheckCircleOutlined />, description: "Your audience is now active and ready for campaign targeting. Full activation typically takes 2-3 business days." }
                      ].map((item, index) => {
                        const stepProgress = hashedEmailList.stepProgress || 0;
                        const status = hashedEmailList.status;
                        
                        // Determine completion based on business logic
                        let isCompleted = false;
                        let isCurrent = false;
                        
                        if (status === "WAITING_FOR_ACTIVATION") {
                          // When waiting for activation, only steps 0 and 1 are completed
                          // Step 2 (Activate in DSP) is the current step being processed
                          isCompleted = index < 2; // Only Upload Emails and Map to Device IDs are completed
                          isCurrent = index === 2; // Activate in DSP is the current step
                        } else if (status === "ACTIVATED" || stepProgress === 4 || hashedEmailList.activated) {
                          // All steps are completed when activated (using new status field, stepProgress, or legacy activated field)
                          isCompleted = true;
                          isCurrent = false;
                        } else {
                          // Normal progression logic
                          isCompleted = stepProgress > index;
                          isCurrent = stepProgress === index;
                        }
                        
                        return (
                          <Step 
                            key={item.title}
                            title={item.title}
                            description={item.description}
                            icon={
                              isCompleted ? (
                                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                              ) : (
                                React.cloneElement(item.icon, { 
                                  style: { 
                                    color: isCurrent ? "#1890ff" : "#bfbfbf",
                                    fontSize: "20px"
                                  } 
                                })
                              )
                            }
                          />
                        );
                      })}
                    </Steps>
                    
                    <div style={{ marginTop: "20px" }}>
                      
                      {/* Step-based Action Buttons */}
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {hashedEmailList.stepProgress === 1 && (
                          <Button
                            type="primary"
                            onClick={() => setStep2DspModalVisible(true)}
                            disabled={loading}
                            icon={<UsergroupAddOutlined />}
                            block
                          >
                            Map to Device IDs
                          </Button>
                        )}
                        
                        {hashedEmailList.stepProgress === 2 && (
                          <Tooltip 
                            title="Check the latest status of this audience to see if device ID mapping is complete and ready for DSP activation."
                            placement="top"
                          >
                            <Button
                              onClick={handleGetLatestStatus}
                              disabled={loading || statusLoading}
                              loading={statusLoading}
                              block
                              style={{ marginBottom: "8px" }}
                              icon={<SyncOutlined />}
                            >
                              Get Latest Status
                            </Button>
                          </Tooltip>
                        )}
                        
                        {hashedEmailList.stepProgress === 3 && (
                          <>
                            <div style={{ padding: "16px", backgroundColor: "#fff7e6", borderRadius: "6px", border: "1px solid #ffd591", marginBottom: "8px" }}>
                              <div style={{ textAlign: "center", marginBottom: "12px" }}>
                                <Text style={{ display: "block", color: "#fa8c16", fontWeight: "bold" }}>
                                  Activating in Progress...
                                </Text>
                                <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                                  Your audience is being processed and will be available for campaign targeting soon.
                                </Text>
                              </div>
                              
                              {/* Status Information */}
                              <div style={{ textAlign: "left", borderTop: "1px solid #ffd591", paddingTop: "12px" }}>
                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#ad6800" }}>Status: </Text>
                                  <Text style={{ color: "#ad6800" }}>
                                    {hashedEmailList.status ? hashedEmailList.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "Waiting For Activation"}
                                  </Text>
                                </div>
                                
                                {hashedEmailList.dsp && (
                                  <div style={{ marginBottom: "8px" }}>
                                    <Text strong style={{ color: "#ad6800" }}>DSP: </Text>
                                    <Text style={{ color: "#ad6800" }}>
                                      {getDSPDisplayName(hashedEmailList.dsp, providersVisible).toUpperCase()}
                                    </Text>
                                  </div>
                                )}
                                
                                {hashedEmailList.dspSegmentIds && hashedEmailList.dspSegmentIds.length > 0 && (
                                  <>
                                    {hashedEmailList.dspSegmentIds[0].segmentId && (
                                      <div style={{ marginBottom: "8px" }}>
                                        <Text strong style={{ color: "#ad6800" }}>Segment ID: </Text>
                                        <Text style={{ color: "#ad6800", wordBreak: "break-all" }}>
                                          {hashedEmailList.dspSegmentIds[0].segmentId}
                                        </Text>
                                      </div>
                                    )}
                                    {hashedEmailList.dspSegmentIds[0].segmentName && (
                                      <div style={{ marginBottom: "8px" }}>
                                        <Text strong style={{ color: "#ad6800" }}>Segment Name: </Text>
                                        <Text style={{ color: "#ad6800" }}>
                                          {hashedEmailList.dspSegmentIds[0].segmentName}
                                        </Text>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <Tooltip 
                              title="Check if this audience has been fully activated and is ready for campaign targeting. This will show if device counts have populated and activation is complete."
                              placement="top"
                            >
                              <Button
                                onClick={handleGetLatestStatus}
                                disabled={loading || statusLoading}
                                loading={statusLoading}
                                block
                                style={{ marginBottom: "8px" }}
                                icon={<SyncOutlined />}
                              >
                                Get Latest Status
                              </Button>
                            </Tooltip>
                          </>
                        )}
                        
                        {(hashedEmailList.stepProgress === 4 || hashedEmailList.activated) && (
                          <div style={{ padding: "16px", backgroundColor: "#f6ffed", borderRadius: "6px", border: "1px solid #b7eb8f" }}>
                            <div style={{ textAlign: "center", marginBottom: "16px" }}>
                              <CheckCircleOutlined style={{ fontSize: "24px", color: "#52c41a", marginBottom: "8px" }} />
                              <Text style={{ display: "block", color: "#52c41a", fontWeight: "bold" }}>
                                Audience Activated!
                              </Text>
                            </div>
                            
                            {/* Activated Audience Details */}
                            <div style={{ textAlign: "left" }}>
                              <Title level={5} style={{ margin: "0 0 12px 0", color: "#52c41a" }}>Activation Details</Title>
                              
                              <div style={{ marginBottom: "8px" }}>
                                <Text strong style={{ color: "#495057" }}>Status: </Text>
                                <Text style={{ color: "#6c757d" }}>
                                  {hashedEmailList.status ? hashedEmailList.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "Activated"}
                                </Text>
                              </div>
                              
                              <div style={{ marginBottom: "8px" }}>
                                <Text strong style={{ color: "#495057" }}>DSP: </Text>
                                <Text style={{ color: "#6c757d" }}>
                                  {hashedEmailList.dsp && typeof hashedEmailList.dsp === "string" ? getDSPDisplayName(hashedEmailList.dsp, providersVisible).toUpperCase() : "TBD"}
                                </Text>
                              </div>
                              
                              <div style={{ marginBottom: "8px" }}>
                                <Text strong style={{ color: "#495057" }}>Segment ID: </Text>
                                <Text style={{ color: "#6c757d", wordBreak: "break-all" }}>
                                  {hashedEmailList.segmentId || hashedEmailList.dspSegmentIds?.[0]?.segmentId || "TBD"}
                                </Text>
                              </div>
                              
                              <div style={{ marginBottom: "8px" }}>
                                <Text strong style={{ color: "#495057" }}>Segment Name: </Text>
                                <Text style={{ color: "#6c757d" }}>
                                  {hashedEmailList.segmentName || hashedEmailList.dspSegmentIds?.[0]?.segmentName || "TBD"}
                                </Text>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Delete button - always visible but disabled when audience is in progress or activated */}
                        {(() => {
                          const isDeletionDisabled = hashedEmailList.status === "Waiting For Activation" || 
                                                   hashedEmailList.status === "Activated" || 
                                                   (hashedEmailList.stepProgress && hashedEmailList.stepProgress >= 3);
                          
                          const tooltipTitle = isDeletionDisabled 
                            ? "Cannot delete an audience that is waiting for activation or already activated. This could interrupt the activation process."
                            : "";

                          return (
                            <Tooltip title={tooltipTitle} placement="top">
                              <span style={{ display: "block" }}>
                                <Popconfirm
                                  title="Are you sure you want to delete this audience?"
                                  onConfirm={handleDeleteAudience}
                                  okText="Yes"
                                  cancelText="No"
                                  disabled={isDeletionDisabled}
                                >
                                  <Button 
                                    danger 
                                    block 
                                    disabled={isDeletionDisabled}
                                    icon={<DeleteOutlined />}
                                  >
                                    Delete Audience
                                  </Button>
                                </Popconfirm>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </Space>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        </Spin>
      </PageContentLayout>

      {/* Status Results Modal */}
      <Modal
        title="Status Update Results"
        open={statusResultsModal}
        onCancel={() => {
          setStatusResultsModal(false);
          setStatusResults(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setStatusResultsModal(false);
            setStatusResults(null);
          }} icon={<CloseOutlined />}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {statusResults && (
          <div>
            {/* Audience Details Card */}
            {statusResults.hashedEmailLists && statusResults.hashedEmailLists.length > 0 && (
              <div>
                <Title level={4} style={{ marginBottom: '16px' }}>Audience Details</Title>
                {statusResults.hashedEmailLists.map((audience, index) => {
                  // Helper functions
                  const getStatusColor = (status) => {
                    switch (status) {
                      case "UPLOADED":
                        return "processing";
                      case "WAITING_FOR_ACTIVATION":
                        return "warning";
                      case "ACTIVATED":
                        return "green";
                      default:
                        return "default";
                    }
                  };
                  
                  const getStatusText = (status) => {
                    switch (status) {
                      case "UPLOADED":
                        return "Uploaded";
                      case "WAITING_FOR_ACTIVATION":
                        return "Waiting For Activation";
                      case "ACTIVATED":
                        return "Activated";
                      default:
                        return status || "Unknown";
                    }
                  };
                  
                  // Determine effective status (handle legacy activated field)
                  const effectiveStatus = audience.status || (audience.activated ? "ACTIVATED" : null);
                  
                  // Get DSP info
                  const dspName = audience.dspSegmentIds && audience.dspSegmentIds.length > 0
                    ? audience.dspSegmentIds[0].dsp
                    : audience.dsp;
                  const displayDspName = getDSPDisplayName(dspName, providersVisible);
                  
                  // Get Segment info
                  let segmentId = audience.segmentId;
                  let segmentName = audience.segmentName;
                  if (!segmentId && audience.dspSegmentIds && audience.dspSegmentIds.length > 0) {
                    segmentId = audience.dspSegmentIds[0].segmentId;
                    segmentName = audience.dspSegmentIds[0].segmentName;
                  }
                  
                  // Get created date
                  const createdAt = audience.createdAt || audience.created_at;
                  
                  // Get process result
                  const processResult = audience.processResult;
                  const resultMessage = processResult 
                    ? (typeof processResult === 'string' ? processResult : processResult.message)
                    : null;
                  
                  return (
                    <Card
                      key={audience.id || index}
                      style={{ 
                        marginBottom: '16px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0'
                      }}
                      bodyStyle={{ padding: '24px' }}
                    >
                      {/* Header Section */}
                      <div style={{ marginBottom: '20px' }}>
                        <Row align="middle" justify="space-between">
                          <Col>
                            <Title level={5} style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                              {audience.name}
                            </Title>
                          </Col>
                          <Col>
                            <Space size="middle">
                              <Badge 
                                count={`${audience.stepProgress || 0}/4`} 
                                style={{ backgroundColor: (audience.stepProgress || 0) === 4 ? '#52c41a' : '#1890ff' }}
                              />
                              {effectiveStatus === 'WAITING_FOR_ACTIVATION' ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    backgroundColor: '#fa8c16',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: '1px solid #fa8c16',
                                    lineHeight: '20px'
                                  }}
                                >
                                  {getStatusText(effectiveStatus)}
                                </span>
                              ) : (
                                <Tag 
                                  color={getStatusColor(effectiveStatus)} 
                                  style={{ 
                                    fontSize: '12px', 
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    fontWeight: 500
                                  }}
                                >
                                  {getStatusText(effectiveStatus)}
                                </Tag>
                              )}
                            </Space>
                          </Col>
                        </Row>
                      </div>

                      {/* Details Grid */}
                      <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              DSP Platform
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {displayDspName ? displayDspName.toUpperCase() : "N/A"}
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Device Count
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {audience.devicesCount ? audience.devicesCount.toLocaleString() : (audience.deviceCount ? audience.deviceCount.toLocaleString() : "N/A")}
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Created Date
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {createdAt ? moment(createdAt).format("MMM DD, YYYY") : "N/A"}
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Data Type
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {audience.typeOfFirstPartyData === "phones" ? "Phone Numbers" : "Email Addresses"}
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Segment Name
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {segmentName && segmentName.trim() !== "" ? segmentName : "N/A"}
                            </Text>
                          </div>
                        </Col>
                      </Row>

                      {/* Segment ID Section (if available) */}
                      {segmentId && segmentId.trim() !== "" && (
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Segment ID
                            </Text>
                          </div>
                          <div style={{ 
                            backgroundColor: '#fafafa', 
                            padding: '12px', 
                            borderRadius: '6px',
                            border: '1px solid #f0f0f0'
                          }}>
                            <Text 
                              code 
                              style={{ 
                                fontSize: '13px',
                                wordBreak: 'break-all',
                                backgroundColor: 'transparent',
                                padding: 0,
                                border: 'none'
                              }}
                            >
                              {segmentId}
                            </Text>
                          </div>
                        </div>
                      )}

                      {/* Message Section (handle new response format) */}
                      {(resultMessage || audience.message) && (
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Status Message
                            </Text>
                          </div>
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '12px', 
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}>
                            <Text style={{ 
                              color: '#666', 
                              fontSize: '13px', 
                              fontStyle: 'italic',
                              lineHeight: '1.5'
                            }}>
                              {audience.message || resultMessage}
                            </Text>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
            
            {(!statusResults.hashedEmailLists || statusResults.hashedEmailLists.length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty description="No detailed results available" />
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* Step 3 DSP Selection Modal */}
      <Modal
        title="Process Device IDs"
        open={processModalVisible}
        onOk={() => handleProcessDeviceIds(selectedDspProvider)}
        onCancel={() => setProcessModalVisible(false)}
        okText="Process"
        cancelText="Cancel"
      >
        <p>Select DSP Provider:</p>
        <Radio.Group
          value={selectedDspProvider}
          onChange={(e) => setSelectedDspProvider(e.target.value)}
        >
          <Radio value="liquidm" disabled={hashedEmailList.typeOfFirstPartyData === "phones"}>
            {getDSPDisplayName('LiquidM', providersVisible)} {hashedEmailList.typeOfFirstPartyData === "phones" && <span style={{ color: "#8c8c8c" }}>(Not available for phone numbers)</span>}
          </Radio>
          <Radio value="nexxen">{getDSPDisplayName('Nexxen', providersVisible)}</Radio>
        </Radio.Group>
      </Modal>

      {/* Step 2 DSP Selection Modal */}
      <Modal
        title="Map to Device IDs"
        open={step2DspModalVisible}
        onOk={handleStep2DspSelection}
        onCancel={() => setStep2DspModalVisible(false)}
        okText="Map to Device IDs"
        cancelText="Cancel"
      >
        <p>Select DSP Provider for Device ID mapping:</p>
        <Radio.Group 
          value={step2DspProvider} 
          onChange={(e) => setStep2DspProvider(e.target.value)}
        >
          <Radio value="liquidm" disabled={hashedEmailList.typeOfFirstPartyData === "phones"}>
            {getDSPDisplayName('LiquidM', providersVisible)} {hashedEmailList.typeOfFirstPartyData === "phones" && <span style={{ color: "#8c8c8c" }}>(Not available for phone numbers)</span>}
          </Radio>
          <Radio value="nexxen">{getDSPDisplayName('Nexxen', providersVisible)}</Radio>
        </Radio.Group>
      </Modal>
    </React.Fragment>
  );
};

export default FirstPartyDataDetailView;
